import { Phone, Sparkles, Check } from "lucide-react";
import { CATERING_INFO, RESTAURANT } from "../data/site";
import { useContent } from "@/context/ContentContext";
import { resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";

export const Catering = () => {
  const { content } = useContent();
  const photos = (content?.catering || []).filter((p) => p.img);
  return (
    <section id="catering" data-testid="catering-section" className="bg-charcoal py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14 md:mb-16">
          <Reveal>
            <span className="font-heading text-gold uppercase tracking-[0.3em] text-sm inline-flex items-center gap-2">
              <Sparkles size={16} /> Catering & Private Events
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-cream leading-tight">
              Bringing Soulful Flavors <br className="hidden md:block" />
              <span className="font-script text-gold-light text-5xl md:text-6xl">Wherever You Celebrate</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 font-body text-xs uppercase tracking-[0.28em] text-gold/80">
              {RESTAURANT.legalName}
            </p>
          </Reveal>
        </div>

        {/* Copy + events */}
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start mb-14 md:mb-16">
          <Reveal className="lg:col-span-3 space-y-5" data-testid="catering-copy">
            {CATERING_INFO.paragraphs.map((p, i) => (
              <p key={i} className="font-body text-cream/75 text-base md:text-lg leading-relaxed">
                {p}
              </p>
            ))}
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2 bg-white/5 border border-gold/25 rounded-3xl p-7 md:p-8">
            <h3 className="font-heading text-xl text-gold-light mb-5">We Cater</h3>
            <ul className="space-y-3">
              {CATERING_INFO.events.map((e) => (
                <li key={e} className="flex items-center gap-3 font-body text-cream/85">
                  <span className="w-6 h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center shrink-0">
                    <Check size={14} />
                  </span>
                  {e}
                </li>
              ))}
            </ul>
            <p
              data-testid="catering-wedding-note"
              className="mt-6 pt-5 border-t border-gold/20 font-body text-sm text-gold-light/90 italic"
            >
              {CATERING_INFO.note}
            </p>
          </Reveal>
        </div>

        {/* Photo gallery (owner-managed) */}
        {photos.length > 0 && (
          <div
            data-testid="catering-gallery"
            className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-14 md:mb-16"
          >
            {photos.map((photo, i) => (
              <Reveal
                key={i}
                delay={i * 0.05}
                data-testid={`catering-photo-${i}`}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
              >
                <img
                  src={resolveImg(photo.img)}
                  alt={photo.caption || "Rainey's catering spread"}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {photo.caption && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/10 to-transparent" />
                    <p className="absolute bottom-0 left-0 p-4 font-body text-cream text-sm md:text-base font-medium">
                      {photo.caption}
                    </p>
                  </>
                )}
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.1} className="text-center">
          <p className="font-script text-gold-light text-3xl md:text-4xl max-w-3xl mx-auto leading-tight">
            {CATERING_INFO.cta}
          </p>
          <a
            data-testid="catering-book-btn"
            href={RESTAURANT.cateringPhoneHref}
            className="mt-8 inline-flex items-center gap-2.5 bg-gold text-charcoal px-9 py-4 rounded-full font-body font-semibold tracking-wide hover:bg-gold-light transition-all duration-300 hover:scale-[1.04] shadow-[0_12px_30px_rgba(168,141,88,0.3)]"
          >
            <Phone size={18} /> Book Catering — {RESTAURANT.cateringPhone}
          </a>
        </Reveal>
      </div>
    </section>
  );
};
